import { Extension } from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'
import { Decoration } from '@tiptap/pm/view'

import { AiSuggestionCache } from './cache'
import { AiSuggestionPlugin, aiSuggestionPluginKey } from './plugin'
import { AiSuggestionRule } from './rules'
import {
  AiSuggestionCustomResolverOptions,
  AiSuggestionModelName,
  AiSuggestionRejection,
  ApplyAllAiSuggestionOptions,
  ApplyAiSuggestionOptions,
  ChunkHtmlOptions,
  GetCustomSuggestionDecorationOptions,
  HtmlChunk,
  Suggestion,
} from './types'
import { defaultChunkHtmlFunction, defaultResolver } from './utils'

import './ai-suggestion.scss'
/**
 * Configuration options for the AI Suggestion extension.
 */
export interface AiSuggestionOptions {
  /**
   * Whether to disable the AI Suggestion extension.
   * @default false
   */
  disabled?: boolean
  /** Rules to be applied during proofreading
   * @default []
   */
  rules: AiSuggestionRule[]
  /** Initial suggestions to display before any proofreading is done
   * @default []
   */
  initialSuggestions: Suggestion[]
  /** Initial rejections to apply before any proofreading is done
   * @default []
   */
  initialRejections: AiSuggestionRejection[]
  /** Function to customize the decoration of suggestions in the editor
   * @default `getDefaultDecorations()`
   * @param args - Options for customizing suggestion decorations.
   * @return An array of `Decoration` objects.
   */
  getCustomSuggestionDecoration: (args: GetCustomSuggestionDecorationOptions) => Decoration[]
  /** Time in milliseconds to wait before reloading suggestions after content changes.
   * @default 800
   */
  debounceTimeout: number
  /** Whether to load suggestions when the editor is initialized
   * @default true
   */
  loadOnStart: boolean
  /** Whether to reload suggestions when the editor content changes
   * @default true
   */
  reloadOnUpdate: boolean
  /** Callback for handling errors when loading suggestions
   * @param error - The error that occurred during suggestion loading.
   * @default console.error
   */
  onLoadSuggestionsError: (error: unknown) => void
  /**
   * The AI model used to proofread the content and generate suggestions.
   * @default "gpt-4o-mini"
   */
  modelName: AiSuggestionModelName
  /**
   * Function to load suggestions from an external source, based
   * on the current editor content and rules. Lets you analyze the
   * content with your own AI model and return suggestions.
   *
   * @param options - Options for resolving suggestions.
   * @return A list of suggestions that should be applied.
   */
  resolver: (options: AiSuggestionCustomResolverOptions) => Promise<Suggestion[]>
  /**
   * The Tiptap AI app ID.
   */
  appId: string
  /**
   * The Tiptap AI token.
   */
  token: string
  /**
   * The base URL for the Tiptap AI API.
   */
  baseUrl: string
  /**
   * Optional context to provide additional information to the AI model
   * for generating suggestions.
   */
  context: string | null
  /**
   * Whether to chunk the editor content and cache the suggestions
   * for each chunk. This allows the extension to reuse the cached suggestions
   * for each chunk when the content is reloaded. This is useful for large
   * documents to improve performance and reduce costs.
   *
   * @default true
   */
  enableCache: boolean
  /**
   * The size of the chunks to split the document into, measured in the amount
   * of top-level child nodes of the editor's content.
   *
   * @default 2
   */
  chunkSize: number
  /**
   * A function that splits the HTML content of the editor into chunks. By
   * default, it splits the HTML into smaller pieces based on the configured
   * chunk size and returns an array of HTML chunks. You can override this
   * behavior by providing your own function.
   *
   * @param options - The options for chunking the HTML.
   * @returns An array of chunks
   * @default `defaultChunkHtmlFunction`
   */
  chunkHtmlFunction: (options: ChunkHtmlOptions) => HtmlChunk[]
}

export const AiSuggestion = Extension.create<AiSuggestionOptions>({
  name: 'aiSuggestion',

  addOptions() {
    return {
      disabled: false,
      rules: [],
      initialSuggestions: [],
      initialRejections: [],
      getCustomSuggestionDecoration: ({ getDefaultDecorations }) => getDefaultDecorations(),
      debounceTimeout: 800,
      loadOnStart: false,
      reloadOnUpdate: false,
      onLoadSuggestionsError: error => {
        console.error('An error occurred while loading suggestions', error)
      },
      modelName: 'gpt-4o-mini',
      resolver: defaultResolver,
      appId: '',
      token: '',
      baseUrl: '',
      context: null,
      enableCache: true,
      chunkSize: 2,
      chunkHtmlFunction: defaultChunkHtmlFunction,
    }
  },

  addStorage() {
    const self = this as any
    const storage = {
      pluginKey: aiSuggestionPluginKey,
      rules: [],
      getSuggestions: () => {
        if (!self.editor?.state) {
          return []
        }
        const pluginState = aiSuggestionPluginKey.getState(self.editor.state)
        return pluginState?.suggestions || []
      },
      getRejections: () => {
        if (!self.editor?.state) {
          return []
        }
        const pluginState = aiSuggestionPluginKey.getState(self.editor.state)
        return pluginState?.rejections || []
      },
      getSelectedSuggestion: () => {
        if (!self.editor?.state) {
          return null
        }
        const pluginState = aiSuggestionPluginKey.getState(self.editor.state)
        if (!pluginState || !pluginState.selectedSuggestionId) {
          return null
        }
        return (
          pluginState.suggestions.find((s: Suggestion) => s.id === pluginState.selectedSuggestionId) || null
        )
      },
      isLoading: false,
      firstLoad: false,
      error: null,
      debouncedFunction: () => { },
      abortController: new AbortController(),
      context: null,
      cache: new AiSuggestionCache(),
    }
    return storage
  },

  addCommands() {
    return {
      loadAiSuggestions: () => ({ tr, dispatch }) => {
        console.log('[Extension] loadAiSuggestions called')
        if (!dispatch) return true

        // Create a new transaction for loading
        const newTr = tr.setMeta(this.storage.pluginKey, {
          type: 'load',
          load: true,
          setSuggestions: true,
          suggestions: [],
          loading: true,
          error: null
        })

        // Dispatch the transaction
        dispatch(newTr)

        return true
      },
      loadAiSuggestionsDebounced: () => () => {
        // Logic to be implemented in the plugin view
        return true
      },
      setAiSuggestions: (suggestions: Suggestion[]) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(this.storage.pluginKey, { setSuggestions: true, suggestions })
        }
        return true
      },
      selectAiSuggestion: (suggestionId: string) => ({ tr, dispatch }) => {
        console.log('[Extension] selectAiSuggestion called', suggestionId)
        const state = this.editor?.state
        const pluginState = state && aiSuggestionPluginKey.getState(state)
        const suggestions: Suggestion[] = pluginState?.suggestions || []
        // const suggestions: Suggestion[] = this.storage.getSuggestions()
        const suggestion = suggestions.find(s => s.id === suggestionId)
        console.log('[Extension] selectAiSuggestion called', suggestionId, suggestions, 'suggestion', suggestion)
        if (dispatch && suggestion) {
          console.log('[Extension] about to dispatch meta:', { selectSuggestion: true, suggestionId })
          const newTr = tr.setMeta(aiSuggestionPluginKey, { selectSuggestion: true, suggestionId })
            .setSelection(TextSelection.create(tr.doc, suggestion.deleteRange.from));
          dispatch(newTr);
        }

        return true
      },
      applyAiSuggestion: (options: ApplyAiSuggestionOptions) => ({ tr, dispatch, state }) => {
        console.log('[Extension] applyAiSuggestion called', options)
        if (!dispatch) {
          return true
        }

        const pluginState = aiSuggestionPluginKey.getState(state)
        if (!pluginState) {
          return true
        }
        const suggestion = pluginState?.suggestions.find(s => s.id === options.suggestionId)

        if (suggestion) {
          const replacement = options.replacementOptionId
            ? suggestion.replacementOptions.find(o => o.id === options.replacementOptionId)
            : suggestion.replacementOptions[0]

          if (replacement) {
            const { from, to } = suggestion.deleteRange
            const { addText, addSlice } = replacement

            // Create a transaction for content update
            if (options.format === 'rich-text' && addSlice) {
              tr.replace(from, to, addSlice)
            } else {
              tr.insertText(addText, from, to)
            }

            // Add meta for suggestion application
            tr.setMeta(aiSuggestionPluginKey, {
              applySuggestion: {
                suggestionId: options.suggestionId,
                replacementOptionId: options.replacementOptionId,
                format: options.format
              }
            })

            // Dispatch the transaction
            dispatch(tr)

            // After applying the suggestion, trigger a state update to refresh decorations
            const updatedSuggestions = pluginState.suggestions.filter(s => s.id !== options.suggestionId)
            const newTr = state.tr.setMeta(aiSuggestionPluginKey, {
              setSuggestions: true,
              suggestions: updatedSuggestions,
              loading: false,
              error: null
            })

            dispatch(newTr)
          }
        }
        return true
      },
      rejectAiSuggestion: (suggestionId: string) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(this.storage.pluginKey, { rejectSuggestion: { suggestionId } })
        }
        return true
      },
      setAiSuggestionRejections: (rejections: AiSuggestionRejection[]) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(this.storage.pluginKey, { setAiSuggestionRejections: true, rejections })
        }
        return true
      },
      applyAllAiSuggestions: (options?: ApplyAllAiSuggestionOptions) => ({ tr, dispatch, state }) => {
        if (!dispatch) {
          return true
        }

        const pluginState = aiSuggestionPluginKey.getState(state)
        const suggestions = pluginState?.suggestions || []
        console.log('[Extension] applyAllAiSuggestions', { suggestions, pluginState })

        // Create a single transaction for all changes
        const changes = suggestions
          .filter((suggestion: Suggestion) => !suggestion.isRejected)
          .sort((a, b) => b.deleteRange.from - a.deleteRange.from) // Process from end to start to maintain positions

        // Apply all changes in a single transaction
        changes.forEach((suggestion: Suggestion) => {
          const replacement = suggestion.replacementOptions[0]
          if (!replacement) {
            return
          }

          const { from, to } = suggestion.deleteRange
          const { addText, addSlice } = replacement

          // Map positions through previous changes
          const mappedFrom = tr.mapping.map(from)
          const mappedTo = tr.mapping.map(to)

          if (options?.format === 'rich-text' && addSlice) {
            tr.replace(mappedFrom, mappedTo, addSlice)
          } else {
            tr.insertText(addText, mappedFrom, mappedTo)
          }
        })

        // Clear suggestions in the same transaction
        tr.setMeta(aiSuggestionPluginKey, { setSuggestions: true, suggestions: [] })

        // Dispatch once with all changes
        dispatch(tr)

        return true
      },
      rejectAllAiSuggestions: () => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(this.storage.pluginKey, { setSuggestions: true, suggestions: [] })
        }
        return true
      },
      setAiSuggestionRules: (rules?: AiSuggestionRule[]) => () => {
        this.storage.rules = rules || []
        return true
      },
      setAiSuggestionContext: (context: string | null) => () => {
        this.storage.context = context
        return true
      },
    }
  },

  addProseMirrorPlugins() {
    return [AiSuggestionPlugin(this.options)]
  },
})
